/**
 * Per-draw visibility of the Change Orders and Lien Waivers sections.
 *
 * A multi-draw report can cover payapps that carry no meaningful change-order or lien-waiver
 * content. Hiding such a draw suppresses its block in both sections and, just as importantly,
 * removes it from everything derived across draws: the single combined comment each section
 * generates, the change-order summary totals and threshold warnings, Action Items, the cover
 * page table, and the exported PDF.
 *
 * The flag lives on the report (`MagicReport.HiddenCoLienDrawIds`) and is chosen in the
 * draw-selection dialog when a report is created or reset. Hidden draws keep their stored data
 * — nothing is deleted — so unhiding one in a later period brings its rows and waiver state back.
 */

/**
 * Reads the stored hidden set into something the sections can use.
 *
 * Two clamps, mirroring `NormalizeHiddenDrawIds` on the backend:
 * - ids outside the covered set are dropped, so a stale id left over from a dropped draw can't
 *   re-hide a draw that later reuses it;
 * - a set covering every draw collapses to empty, so a malformed value can never blank both
 *   sections with no way for the user to get them back.
 */
export const resolveHiddenCoLienDrawIds = (hidden: string[] | undefined, coveredDrawIds: (string | undefined)[]): Set<string> => {
  const covered = coveredDrawIds.filter((id): id is string => Boolean(id));
  const resolved = new Set((hidden || []).filter(id => Boolean(id) && covered.includes(id)));
  if (covered.length > 0 && resolved.size >= covered.length) return new Set<string>();
  return resolved;
};

/** Whether a draw's Change Orders / Lien Waivers content is suppressed. */
export const isCoLienHidden = (hidden: Set<string>, drawId?: string): boolean => Boolean(drawId) && hidden.has(drawId as string);

/** The covered draws that still show their Change Orders / Lien Waivers blocks, in report order. */
export const filterVisibleCoLienDraws = <T extends { drawId?: string }>(draws: T[], hidden: Set<string>): T[] =>
  draws.filter(draw => !isCoLienHidden(hidden, draw.drawId));
